import React, { useEffect } from 'react'
import { Paper } from '@material-ui/core'
import ActionBar from 'components/ActionBar'
import Tooltip from '@material-ui/core/Tooltip'
import { useHistory, useLocation } from 'react-router-dom'
import ViewHeader from 'components/ViewHeader'
import { useDispatch, useSelector } from 'react-redux'
import ResourceNotFound from 'components/ResourceNotFound'
import Loading from 'components/Loading'
import { EditOutlined as EditIcon, DeleteOutlineOutlined as DeleteIcon } from '@material-ui/icons'
import { useConfirm } from 'material-ui-confirm'

import getTableOptions from 'utils/getTableOptions'
import venueSlice, {
  fetchVenues,
  deleteVenueById,
  checkVenueExistsInTalks,
  checkVenueExistsInEvents,
  bulkInsertVenues
} from 'store/venue'
import { toast } from 'react-toastify'
import BasicTable from 'components/BasicTable'
import { renderCol } from 'utils/renderColumns'
import FileUpload from 'components/FileUpload'
import Typography from '@material-ui/core/Typography'
import { Box } from '@mui/material'
import { makeStyles } from '@material-ui/core/styles'
import { parseCsvData } from 'utils'

const VenueListing = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const confirm = useConfirm()
  const history = useHistory()
  const location = useLocation()
  const { user } = useSelector((state) => state.auth)
  const { venues, currentPage, pageSize, inProgress } = useSelector((state) => state.venue)

  useEffect(() => {
    const unsubVenues = fetchVenues(user?.conferenceId, (venuesData) => {
      dispatch(venueSlice.actions.setVenues(venuesData))
    })

    return () => unsubVenues && unsubVenues()
  }, [dispatch, user?.conferenceId])

  const uploadVenuesData = async (venuesList) => {
    try {
      const parsedVenues = parseCsvData(venuesList)
      if (!parsedVenues.length) {
        toast.error('No venue exists to upload.')
        return
      }
      const existingVenues = venues?.map((venue) => {
        return `${venue.title?.toLowerCase()}, ${venue.floorNum?.toLowerCase()}, ${venue.roomNum?.toLowerCase()}`
      })
      const uniqueVenues = parsedVenues
        ?.map(({ title, location, room }) => ({
          title,
          floorNum: location,
          roomNum: room,
          conferenceId: user?.conferenceId
        }))
        ?.filter(
          ({ title, floorNum, roomNum }) =>
            !existingVenues.includes(
              `${title?.toLowerCase()}, ${floorNum?.toLowerCase()}, ${roomNum?.toLowerCase()}`
            )
        )
      await dispatch(bulkInsertVenues(uniqueVenues))
      const uniqueCount = uniqueVenues.length
      const existingCount = parsedVenues.length - uniqueCount

      if (uniqueCount && !existingCount) {
        toast.success(`${uniqueCount} Venue(s) imported successfully.`)
      } else if (!uniqueCount && existingCount) {
        toast.error(`${existingCount} Venue(s) have been excluded due to duplication.`)
      } else {
        toast.success(`${uniqueCount} Venue(s) imported successfully.`)
        toast.error(`${existingCount} Venue(s) have been excluded due to duplication.`)
      }
    } catch (err) {
      toast.error('Something went wrong while creating venues.')
    }
  }

  const columns = [
    {
      name: 'Title',
      label: 'Title',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'Location',
      label: 'Location',
      options: {
        filter: true,
        sort: false
      }
    },
    {
      name: 'Room #',
      label: 'Room #',
      options: {
        filter: true,
        sort: false
      }
    },
    {
      name: 'Actions',
      label: 'Actions',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => renderCol(value),
        customHeadLabelRender: (meta) => renderCol(meta.label)
      }
    }
  ]
  const formatTableData = (venues) =>
    venues.map((venue) => [
      venue.title,
      venue.floorNum,
      venue.roomNum,
      <>
        <Tooltip title="Edit">
          <EditIcon
            color="action"
            style={{ cursor: 'pointer' }}
            onClick={() => history.push(`${location.pathname}/edit/${venue.id}`)}
          />
        </Tooltip>
        <Tooltip title="Delete">
          <DeleteIcon
            color="action"
            style={{ marginLeft: 5, cursor: 'pointer' }}
            onClick={async () => {
              try {
                await confirm({
                  title: 'Delete Venue',
                  description: 'Are you sure you want to delete this Venue ?'
                })
                const isVenueExistInTalk = await dispatch(checkVenueExistsInTalks(venue.id))
                const isVenueExistInEvents = await dispatch(checkVenueExistsInEvents(venue.id))

                if (isVenueExistInTalk || isVenueExistInEvents) {
                  toast(
                    `Can not delete this Venue because it is referenced in other Event(s) or Talk(s).In order to delete this first remove it from all Events/Talks.`,
                    {
                      type: 'error',
                      autoClose: 5500
                    }
                  )
                } else {
                  await dispatch(deleteVenueById(user?.conferenceId, venue.id))
                }
              } catch {
                // do nothing
              }
            }}
          />
        </Tooltip>
      </>
    ])

  const options = getTableOptions()

  return (
    <>
      <ViewHeader title="Venues">
        {inProgress && <Loading />}
        <ActionBar
          actionText="New Venue"
          onAction={() => history.push(`${location.pathname}/new`)}
          searchPlaceholder="Search Venues"
        />
      </ViewHeader>
      <FileUpload headerText="Create Venues from file" onUpload={uploadVenuesData} />
      <Box className={classes.infoBox}>
        <Typography className={classes.infoText}>
          Please ensure that the CSV file you upload includes these columns titled{' '}
          <span className={classes.bold}>&apos;Title, Location and Room&apos;</span> for
          venue&apos;s data.
        </Typography>
      </Box>

      {venues && venues.length ? (
        <Paper variant="outlined" style={{ marginTop: 20 }}>
          <BasicTable
            data={formatTableData(venues)}
            columns={columns}
            options={{
              ...options,
              onChangePage: (page) => dispatch(venueSlice.actions.setCurrentPage(page)),
              onChangeRowsPerPage: (size) => dispatch(venueSlice.actions.setPageSize(size)),
              page: currentPage,
              rowsPerPage: pageSize
            }}
          />
        </Paper>
      ) : (
        !inProgress && <ResourceNotFound message="No venue available" />
      )}
    </>
  )
}

export default VenueListing

const useStyles = makeStyles({
  infoBox: {
    marginTop: '16px',
    marginLeft: '10px',
    backgroundColor: '#caedf5',
    padding: '16px',
    width: '80%',
    borderRadius: '10px'
  },
  infoText: {
    fontSize: '14px'
  },
  bold: {
    fontWeight: '600'
  }
})
